import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Warning, XCircle, MinusCircle, TrendUp, TrendDown } from "@phosphor-icons/react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatValue, formatPeriod, STATUS_META } from "@/lib/helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import KpiDetailDialog from "@/components/KpiDetailDialog";

const SUMMARY_CARDS = [
  { key: "verde", label: "En meta", Icon: CheckCircle },
  { key: "amarillo", label: "En riesgo", Icon: Warning },
  { key: "rojo", label: "Fuera de meta", Icon: XCircle },
  { key: "sin_datos", label: "Sin datos", Icon: MinusCircle },
];

export default function Dashboard() {
  const { companyIdsParam, selectedCompanyIds, companies, period } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [selectedKpi, setSelectedKpi] = useState(null);

  useEffect(() => {
    if (!companyIdsParam) { setData(null); setLoading(false); return; }
    setLoading(true);
    const p = period ? `&period=${period}` : "";
    api
      .get(`/dashboard?company_ids=${companyIdsParam}${p}`)
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [companyIdsParam, period]);

  const categories = useMemo(() => {
    const set = new Set((data?.kpis || []).map((k) => k.category).filter(Boolean));
    return Array.from(set);
  }, [data]);

  const filtered = (data?.kpis || []).filter((k) => category === "all" || k.category === category);
  const scopeLabel =
    selectedCompanyIds.length === companies.length ? "Todas las empresas (consolidado)"
    : selectedCompanyIds.length === 1
      ? companies.find((c) => c.id === selectedCompanyIds[0])?.name
      : `${selectedCompanyIds.length} empresas (consolidado)`;

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard Ejecutivo</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {scopeLabel} · {formatPeriod(data?.period || period)}
          </p>
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 w-[230px]" data-testid="category-filter">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="summary-cards">
        {SUMMARY_CARDS.map(({ key, label, Icon }) => {
          const meta = STATUS_META[key];
          return (
            <div key={key} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md">
              <div className="bg-primary px-4 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-primary-foreground">{label}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-4">
                <p className={`font-heading text-4xl font-bold ${meta.text}`} data-testid={`summary-${key}`}>
                  {data?.summary?.[key] ?? 0}
                </p>
                <Icon weight="duotone" size={26} className={meta.text} aria-hidden="true" />
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI grid */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
          No hay indicadores para mostrar. Captura información en el módulo de Captura Manual.
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="kpi-dashboard-grid"
        >
          {filtered.map((k, i) => {
            const meta = STATUS_META[k.status];
            return (
              <motion.button
                key={k.kpi_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.02, 0.4) }}
                onClick={() => setSelectedKpi(k.kpi_id)}
                data-testid={`kpi-card-${k.code}`}
                className="group flex flex-col rounded-xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:border-foreground/30 hover:shadow-lg"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {k.category}
                  </span>
                  <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                </div>
                <p className="mt-2 text-sm font-medium leading-snug text-foreground">{k.name}</p>
                <div className="mt-auto pt-4">
                  <p className="font-heading text-3xl font-bold tracking-tight">
                    {formatValue(k.value, k.unit)}
                    <span className="ml-1 text-sm font-normal text-muted-foreground">{k.unit}</span>
                  </p>
                  <Badge variant="outline" className={`mt-2 ${meta.bg} ${meta.text} ${meta.border}`}>
                    {meta.label}
                  </Badge>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      <KpiDetailDialog
        kpiId={selectedKpi}
        companyIdsParam={companyIdsParam}
        open={!!selectedKpi}
        onOpenChange={(o) => !o && setSelectedKpi(null)}
      />
    </div>
  );
}
